import { color } from "./colors";
import { borderRadius, spacing } from "./spacing";
import { typography } from "./typography";

export const OMNI_THEME_FONT_URL =
  "https://cdn.jsdelivr.net/npm/@fontsource/inter@4.5.14/files/inter-latin-variable-full-normal.woff2";

// Omni's theme keys (https://docs.omni.co/embed/customization/themes).
// atlas-api refuses any key outside this list.
export const OMNI_THEME_KEYS = [
  "dashboard-background",
  "dashboard-tile-background",
  "dashboard-control-background",
  "dashboard-control-popover-background",
  "dashboard-filter-input-background",
  "dashboard-tile-text-body-color",
  "dashboard-tile-text-secondary-color",
  "dashboard-tile-border-color",
  "dashboard-tile-title-text-color",
  "dashboard-control-border-color",
  "dashboard-control-text-color",
  "dashboard-control-placeholder-color",
  "dashboard-control-label-color",
  "dashboard-control-outline-color",
  "dashboard-control-popover-text-color",
  "dashboard-control-popover-secondary-text-color",
  "dashboard-control-popover-link-color",
  "dashboard-control-popover-divider-color",
  "dashboard-control-popover-border-color",
  "dashboard-filter-input-border-color",
  "dashboard-filter-input-text-color",
  "dashboard-filter-input-placeholder-color",
  "dashboard-filter-input-icon-color",
  "dashboard-filter-input-outline-color",
  "dashboard-filter-input-accent-color",
  "dashboard-filter-input-accent-invert-color",
  "dashboard-filter-input-token-color",
  "dashboard-filter-input-token-text-color",
  "dashboard-key-color",
  "dashboard-key-text-color",
  "dashboard-button-transparent-text-color",
  "dashboard-button-transparent-interactive-color",
  "dashboard-menu-item-interactive-color",
  "dashboard-page-padding",
  "dashboard-tile-margin",
  "dashboard-tile-border-radius",
  "dashboard-tile-border-width",
  "dashboard-tile-title-font-size",
  "dashboard-control-radius",
  "dashboard-control-popover-radius",
  "dashboard-filter-input-radius",
  "dashboard-button-radius",
  "dashboard-tile-title-font-family",
  "dashboard-tile-text-body-font-family",
  "dashboard-tile-text-code-font-family",
  "dashboard-tile-title-font-weight",
  "dashboard-tile-border-style",
  "dashboard-tile-shadow",
] as const;

export type OmniThemeKey = typeof OMNI_THEME_KEYS[number];

// Omni's rem base may differ from ours, so lengths are sent in px.
const px = (rem: number): string => `${rem * 16}px`;

const font = `url(${OMNI_THEME_FONT_URL})`;
const radius = px(borderRadius.subtle);

export const omniTheme: Partial<Record<OmniThemeKey, string>> = {
  "dashboard-background": color.background.default,
  "dashboard-page-padding": px(spacing.md),
  "dashboard-tile-margin": px(spacing.md),
  "dashboard-tile-shadow": "none",
  "dashboard-tile-border-style": "solid",
  "dashboard-tile-border-width": "1px",

  "dashboard-tile-background": color.background.surface,
  "dashboard-control-background": color.background.surface,
  "dashboard-control-popover-background": color.background.surface,
  "dashboard-filter-input-background": color.background.surface,

  "dashboard-tile-border-color": color.border.muted,
  "dashboard-control-popover-divider-color": color.border.muted,
  "dashboard-menu-item-interactive-color": color.border.muted,

  "dashboard-tile-border-radius": radius,
  "dashboard-control-radius": radius,
  "dashboard-control-popover-radius": radius,
  "dashboard-filter-input-radius": radius,
  "dashboard-button-radius": radius,

  "dashboard-tile-title-font-size": px(typography.fontSize.md),
  "dashboard-tile-title-font-weight": String(typography.fontWeight.semiBold),
  "dashboard-tile-title-font-family": font,
  "dashboard-tile-text-body-font-family": font,

  "dashboard-tile-title-text-color": color.foreground.default,
  "dashboard-tile-text-body-color": color.foreground.default,
  "dashboard-control-text-color": color.foreground.default,
  "dashboard-control-popover-text-color": color.foreground.default,
  "dashboard-filter-input-text-color": color.foreground.default,

  "dashboard-tile-text-secondary-color": color.foreground.muted,
  "dashboard-control-placeholder-color": color.foreground.muted,
  "dashboard-control-label-color": color.foreground.muted,
  "dashboard-control-popover-secondary-text-color": color.foreground.muted,
  "dashboard-filter-input-placeholder-color": color.foreground.muted,
  "dashboard-filter-input-icon-color": color.foreground.muted,

  "dashboard-control-border-color": color.border.default,
  "dashboard-control-popover-border-color": color.border.default,
  "dashboard-filter-input-border-color": color.border.default,

  "dashboard-control-outline-color": color.border.focus,
  "dashboard-filter-input-outline-color": color.border.focus,

  "dashboard-control-popover-link-color": color.foreground.primaryAction,
  "dashboard-filter-input-accent-color": color.foreground.primaryAction,
  "dashboard-key-color": color.foreground.primaryAction,
  "dashboard-button-transparent-text-color": color.foreground.primaryAction,

  "dashboard-filter-input-accent-invert-color":
    color.foreground.onPrimaryAction,
  "dashboard-key-text-color": color.foreground.onPrimaryAction,

  "dashboard-filter-input-token-color": color.background.selected,
  "dashboard-button-transparent-interactive-color": color.background.selected,
  "dashboard-filter-input-token-text-color": color.foreground.default,
};
